/**
 * ── A CASCA DO DICIONÁRIO: O QUE O PEDAÇO DE ENTRADA LÊ ────────────────────
 *
 * Estas são as frases que a MOLDURA da área do aluno desenha — a barra, o
 * menu, as portas de entrada, o cadeado, a espera, a recusa de acesso. Elas
 * viajam no `index-*.js`, porque é lá que o código que as lê vive.
 *
 * ## Por que existe uma casca, e o que a define
 *
 * O dicionário inteiro são 533 chaves (~34 kB de fonte), e o pedaço de
 * entrada do aluno **lia 100 delas**. As outras 433 viajavam em toda visita
 * para serem usadas por telas que descem por `import()` — `Comunidade`,
 * `SalaDeAula`, `Agenda`, `Trilhas` — ou por nenhuma tela, porque nascem no
 * servidor (os selos, o postal da conquista, as recusas da comunidade).
 *
 * O critério não é opinião: é **medido no artefacto construído**. Toda chave
 * que o `index-*.js` cita como literal está aqui, e a cerca
 * `que-chave-vive-em-que-pedaco` falha se alguma deixar de estar. Das 100,
 * 64 são leituras medidas e 36 entraram por serem da MESMA FAMÍLIA de uma
 * lida (`nav`, `acesso`, `cadeado`, `encontro`, `tranca`, `lancamento`,
 * `suporte`, `estado`, `geral`, `busca`): uma chave a mais na casca custa
 * bytes, uma a menos custa **texto cru na cara do aluno**, e os dois preços
 * não se comparam.
 *
 * ## Isto NÃO parte o dicionário do servidor
 *
 * `pt-BR.ts` continua a exportar `pt` com as 533 chaves, compondo esta folha
 * com `conteudo.ts`. Quem traduz no servidor — e são ~25 lugares com
 * `tradutorDe(null)`, incluindo e-mails e o certificado — não vê diferença
 * nenhuma. A partição existe para o BUNDLER, não para a regra.
 */
export const CASCA = {
  /* ── A barra e a navegação ──────────────────────────────────────────── */
  'nav.areas': 'Áreas',
  'nav.conta': 'Conta',
  'nav.sair': 'Sair',
  'nav.menu': 'Menu',
  'nav.procurar': 'Procurar',
  'nav.minhasAulas': 'Minhas aulas',
  'nav.trilhas': 'Trilhas',
  'nav.agenda': 'Agenda',
  'nav.comunidade': 'Comunidade',
  'nav.perfil': 'Seu perfil',
  'nav.voltar': 'Voltar',
  'nav.secoesDaArea': 'Seções da área',
  'nav.idioma': 'Idioma',

  /* ── A vitrine ──────────────────────────────────────────────────────── */
  'vitrine.continuar': 'Continue de onde parou',

  /* ── A conta ────────────────────────────────────────────────────────── */
  'conta.email': 'E-mail',
  'conta.senha': 'Senha',

  /* ── Estados e recusas ──────────────────────────────────────────────── */
  'estado.carregando': 'Carregando…',
  'estado.erro': 'Não deu para carregar. Tente de novo.',
  'estado.semConexao': 'Sem conexão. Verifique a sua internet.',
  'estado.tenteDeNovo': 'Tentar de novo',
  'estado.nadaAqui': 'Não há nada aqui ainda.',

  /* ── O acesso ───────────────────────────────────────────────────────── */
  'acesso.entrar': 'Entrar',
  'acesso.cursoTrancado': 'Este curso ainda não está liberado para você.',
  'acesso.bloqueado': 'Seu acesso está bloqueado.',

  /* ── Segundo lote: comunidade, agenda, leitor e gamificação ───────── */
  'acesso.cursoNaoAbriu': 'Este curso ainda não abriu.',
  'acesso.criarSenha': 'Crie uma senha',
  'acesso.documento': 'CPF, CNPJ ou documento (opcional)',
  'geral.fechar': 'Fechar',
  'geral.cancelar': 'Cancelar',
  'geral.confirmar': 'Confirmar',

  /* ── Terceiro lote: comunidade, agenda, aula, trilha e assistente ───────── */
  'agenda.agendado': 'Agendado',
  'estado.carregandoAulas': 'Carregando as suas aulas…',
  'estado.carregandoCurso': 'Carregando o conteúdo do curso…',
  'acesso.semConta': 'Ainda não tem uma conta nesta área?',
  'acesso.jaEstaNaConta': 'A comunidade e os cursos abertos já estão na sua conta.',

  /* ── Quarto lote: acesso, conta, prova, certificado, trilha, leitor e termos ───────── */
  'acesso.codigo': 'Código',
  'acesso.entrarComCodigo': 'Entrar com meu código',
  'acesso.jaTenhoSenha': 'Já tenho senha',
  'acesso.jaTenhoCodigo': 'Já tenho o código — definir a senha',
  'acesso.esqueciSenha': 'Esqueci minha senha',
  'acesso.criarMinhaConta': 'Criar minha conta',
  'acesso.entrarNaArea': 'Entrar na área',
  'acesso.entrarNaMinhaConta': 'Entrar na minha conta',
  'acesso.enderecoDaArea': 'Endereço da área (a parte depois de /a/ no link do curso)',
  'acesso.irParaOCurso': 'Ir para o curso',
  'vitrine.emDestaque': 'Em destaque',
  'vitrine.amostra': 'Amostra',
  'vitrine.naoIncluido': 'Não incluído',
  'vitrine.aulasAbremJunto': 'As aulas abaixo abrem junto com o curso.',
  'busca.fechar': 'Fechar a busca',
  'busca.nadaSobreIsso': 'Não achei nada sobre isso.',
  'geral.deixarComoEstava': 'Deixar como estava',
  'suporte.prazoTerminou':
    'O prazo desta sessão de suporte terminou. Nada foi gravado na conta do aluno.',

  /* ── Quinto lote: busca, assistente, perguntas, rendimento e acesso ───────── */
  'nav.asMinhasAulas': 'As minhas aulas',
  'nav.aulas': 'Aulas',
  'nav.contaELinks': 'Conta e links da área',
  'nav.ondeQuerEntrar': 'Onde você quer entrar?',
  'nav.verCursosDaArea': 'Ver os cursos da área',
  'nav.voltarParaCursos': 'Voltar para os meus cursos',
  'nav.voltarParaEntrar': 'Voltar para entrar',
  'busca.procurarNasAulas': 'Procurar nas suas aulas',
  'busca.oQueProcura': 'O que você está procurando?',
  'busca.porData': 'Procurar por data',
  'acesso.pedirCodigo': 'Pedir o código',
  'acesso.recebeuCodigo': 'Recebeu um código de acesso?',
  'acesso.recebiCodigo': 'Recebi um código de acesso',
  'acesso.entrarComCodigoDeAcesso': 'Entrar com o código de acesso',
  'acesso.quemNaoTemSenha':
    'Quem ainda não definiu uma senha entra com ele — e escolhe a senha nessa hora.',
  'acesso.contaDaAcesso': 'Sua conta dá acesso à comunidade e aos cursos abertos desta área.',
  'acesso.trancado': 'Trancado',
  'acesso.paraAbrir': 'Para abrir',
  'acesso.naoTemAinda': 'Ele existe, e você ainda não tem. Veja como conseguir.',
  'trilha.titulo': 'Trilha',
  'estado.recarregar': 'Recarregar a página',
  'geral.tituloOpcional': 'Título (opcional)',
  'suporte.vendoComo': 'Você está vendo a área como',

  /* ── Sexto lote: o que faltava ───────── */
  'vitrine.voceAindaNaoTem': 'Você ainda não tem',

  /* ── O lançamento do curso (core/area/lancamento-do-curso.ts) ── */
  'lancamento.jaAbriu': 'Já abriu.',
  'lancamento.abreHojeAs': 'Abre hoje às {hora}h',
  'lancamento.abreHojeAsMin': 'Abre hoje às {hora}h{minuto}',
  'lancamento.abreAmanha': 'Abre amanhã',
  'lancamento.abreEmDias': 'Abre em {dias} dias',
  'lancamento.abreEmData': 'Abre em {dia} de {mes}',

  /* ── A agenda: a hora relativa (core/area/agenda.ts) ── */
  'agenda.acontecendoAgora': 'acontecendo agora',
  'agenda.jaAconteceu': 'já aconteceu',
  'agenda.comecaEmInstantes': 'começa em instantes',

  /* ── Quanto falta (core/area/agenda.ts) ── */
  'agenda.emMinutos': 'em {n} minutos',
  'agenda.emHoras': 'em {n} horas',
  'agenda.emDias': 'em {n} dias',

  /* ── O cartão do encontro (aluno/agenda/peso-do-encontro.ts) ── */
  'encontro.hoje': 'hoje',
  'encontro.eAmanha': 'é amanhã',
  'encontro.faltamDias': 'faltam {dias} dias',

  /* ── A espera do curso (aluno/lib/lancamento.ts) ── */
  'lancamento.semData': 'Este curso abre em breve.',

  /* ── O cadeado da aula (aluno/lib/api.ts) ── */
  'cadeado.abreHoje': 'Abre hoje',
  'cadeado.aindaNaoDisponivel': 'Ainda não disponível',
  'cadeado.abreEmData': 'Abre em {data}',

  /* ── As frases que estavam partidas por uma expressão JSX ── */
  'nav.verO': 'Ver o {titulo}',
  'nav.ola': 'Olá, {nome}',
  'tranca.irPara': 'Ir para «{titulo}»',
  'nav.oCurso': 'curso',
  /* ── O que um MÓDULO PARTILHADO traduz com a casca por base ──────────
       `core/area/agenda.ts`, `lancamento-do-curso.ts` e `prateleira-itens.ts`
       são importados pelo pedaço de entrada do aluno, e o `t` deles cai na
       casca quando ninguém passa um tradutor. Logo toda chave que eles
       traduzem mora AQUI — as outras sairiam cruas. As do plural já estavam
       por serem lidas na tela; as do singular e as de `item.*` não, e eram
       `em {n} minuto` a virar `agenda.emMinuto` na primeira vez que a
       agenda tivesse um encontro a um minuto de distância. */
  'agenda.emMinuto': 'em {n} minuto',
  'agenda.emHora': 'em {n} hora',
  'agenda.emDia': 'em {n} dia',
  'item.umaAula': '{n} aula',
  'item.aulas': '{n} aulas',
  'item.umCurso': '{n} curso',
  'item.cursos': '{n} cursos',

  /* ── Convertidas em 24/10/2026: prosa que a varredura nao via ── */
  'prova.irParaAvaliacao': 'Ir para a avaliação',
  'acesso.semAreaAindaAguarde':
    'Você ainda não tem acesso a nenhuma área. Se acabou de comprar, aguarde alguns minutos.',
  'acesso.deOutroAcesso':
    'Ele é de outro acesso. Se você acha que deveria tê-lo, fale com quem vendeu o curso.',
  'nav.voltarAoInicio': 'Voltar ao início',

  /* ── A leva 2 do dicionário, 26/10/2026 ─────────────────────────────── */
  'acesso.pedimosCodigoNovo':
    'Pedimos um código novo para quem cuida do seu curso enviar. Ele chega pelo canal que essa pessoa configurou — e-mail ou WhatsApp — e não pelo Vestigo.',
  'acesso.linkPodeTerSidoDesligado':
    'O link pode ter sido desligado por quem cuida do curso, ou o endereço pode estar diferente do que veio no convite.',
  'suporte.nadaEGravadoNaConta': '— nada aqui é gravado na conta dele.',
  'trilha.aSuaTrilha': 'A sua trilha',

  /* ── O que o PEDAÇO DE ENTRADA lê (leva 4) ─────────────────────── */
  'assistente.umInstante': 'Um instante…',
  'estado.semAcessoAoCurso': 'Você não tem acesso a este curso.',
  'estado.naoDeuParaEntrar': 'Não foi possível entrar.',
  'estado.cursoNaoAbriu': 'Este curso ainda não abriu.',
  'estado.aulaNaoAbriu': 'Esta aula ainda não abriu.',
  'cerca.naoAbriuDireito': '{oQue} não abriu direito.',
  'cerca.estaParteDaTela': 'Esta parte da tela',
  'cerca.recarregarAPagina': 'Recarregar a página',
  'cerca.faixaDeSuporte': 'A faixa de suporte',
  'cerca.avisoDosTermos': 'O aviso dos termos',
  'cerca.conviteDoInstagram': 'O convite do Instagram',
  'cerca.rolagemDaPagina': 'A rolagem da página',
  'cerca.aArea': 'A área',
  'cerca.estaAula': 'Esta aula',
  /* ── Mudadas da folha do CONTEÚDO em 26/10/2026 ───────────────────────
     Dezenove chaves que o pedaço de ENTRADA lê e que viviam na folha que
     desce com a tela: a porta flutuante do assistente, a faixa da sessão
     de suporte, a vitrine, o portão dos termos e as frases de falha do
     estado. Enquanto a folha não desce, uma chave de conteúdo cai na
     própria chave — e isso é texto cru na cara de quem abre a área.
     Quem mede é `que-chave-vive-em-que-pedaco`, no artefacto. */
  'assistente.perguntar': 'Perguntar',
  'suporte.sessaoDeSuporte': 'sessão de suporte',
  'suporte.sairEVoltarAoPainel': 'Sair e voltar ao painel',
  'suporte.sairDaSimulacao': 'Sair da simulação',
  'vitrine.vocAindaNaoTem': 'Você ainda não tem',
  'vitrine.voceComecou': 'você começou',
  'vitrine.percentAssistido': '{percent}% assistido',
  'vitrine.aSeguir': 'a seguir: {titulo}',
  'vitrine.verATrilhaEmMinhasAulas': 'ver a trilha em Minhas aulas',
  'assistente.oQueFicouSemEntender': 'O que ficou sem entender?',
  'assistente.pergunteOQueQuiser': 'Pergunte o que quiser',
  'assistente.naoAchei': 'Não achei isso nas suas aulas.',
  'assistente.procureiEmTodos':
    'Procurei em todos os seus cursos. Tente com outras palavras — ou leve a pergunta para a Comunidade, onde o tutor e os colegas respondem.',
  'termos.atualizadosAgora':
    'Os termos foram atualizados agora. Leia a versão nova antes de aceitar.',
  'termos.naoDeuParaRegistrar': 'Não foi possível registrar o aceite.',
  'estado.naoDeuParaCompletar': 'Não foi possível completar a ação.',
  'estado.naoDeuParaCarregar': 'Não foi possível carregar.',
  'suporte.motivoDaSessao':
    'Esta é uma sessão de suporte: ela mostra a área como o aluno a vê, e não altera nada na conta dele.',
  'assistente.perguntarANeura': 'Perguntar — {nome}, a tutora dos seus cursos',
  /* ── Mudadas da folha do CONTEÚDO em 26/10/2026 ───────────────────────
     Duas chaves que o pedaço de ENTRADA lê e que viviam na folha que
     desce com a tela: a porta flutuante do assistente, a faixa da sessão
     de suporte, a vitrine, o portão dos termos e as frases de falha do
     estado. Enquanto a folha não desce, uma chave de conteúdo cai na
     própria chave — e isso é texto cru na cara de quem abre a área.
     Quem mede é `que-chave-vive-em-que-pedaco`, no artefacto. */
  'estado.trilhaNaoAbriu': 'Esta trilha ainda não abriu.',
  'suporte.sessaoEncerrada': 'sessão encerrada',
} as const;
